import { Button, Select, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UndoOutlined } from '@ant-design/icons';
import { EligibleCandidatesTable } from './EligibleCandidatesTable';
import { BATCH_HOME } from '../../../../shared/Routes';
import { useEligibleCandidates } from './useEligibleCandidates';
import { useBatchId } from '../../../../hooks/useBatchId';
import LeftArrowWhite from '../../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../../assets/images/filter-icon.svg';
import isEmpty from 'lodash/isEmpty';
import Icon from '../../../../components/ui-components/Icon';
import { FilterEligibleCandidates } from './FilterElgibleCandidates';
import EmptyList from '../../../../components/EmptyList';

const { Text } = Typography;

export const ScreeningEligibleCandidates = () => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage = i18n.language === 'ta' || i18n.language === 'ma';
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const [isFilterApplied, setIsFilterApplied] = useState(false);
	const [selectedBatchId, setSelectedBatchId] = useState<number[]>([]);
	const [resetKey, setResetKey] = useState(0);
	const {
		applicationSummaryData,
		onEligibleCandidatesChange,
		eligibleCandidatesFilter,
		applicationPaging,
		onApplicationPageChange,
		onSubmitClick,
		setEligibleCandidatesIds,
		batchData,
	} = useEligibleCandidates();
	const { navigateToRoute } = useBatchId();
	const { applicationSummary, pagination } = applicationSummaryData;

	const { batches } = batchData;
	const batchOptions = batches?.map((batch) => ({
		label: batch.name,
		value: batch.id,
	}));

	const handleBackClick = useCallback(() => {
		navigateToRoute(BATCH_HOME);
	}, [navigateToRoute]);

	const handleFilterPopup = () => {
		setIsFilterModalOpen(true);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const handleResetFilters = useCallback(() => {
		onEligibleCandidatesChange({ batchIds: selectedBatchId });
		setResetKey((prev) => prev + 1);
	}, [onEligibleCandidatesChange, selectedBatchId]);

	const handleBatchChange = useCallback(
		(value: number[]) => {
			setSelectedBatchId(value);
			onEligibleCandidatesChange({ batchIds: value });
		},
		[onEligibleCandidatesChange]
	);

	useEffect(() => {
		const { batchIds, ...rest } = eligibleCandidatesFilter;
		setIsFilterApplied(!isEmpty(rest));
	}, [eligibleCandidatesFilter]);

	useEffect(() => {
		if (batchOptions?.length && selectedBatchId.length === 0) {
			// Only set default if nothing is selected yet
			setSelectedBatchId([batchOptions[0].value || 0]);
			onEligibleCandidatesChange({ batchIds: [batchOptions[0].value || 0] });
		}
	}, [batchOptions, selectedBatchId, onEligibleCandidatesChange]);
	return (
		<div className="flex flex-col min-w-[350px] max-w-[480px] sm:min-w-0 sm:max-w-none px-[16px] py-[8px]">
			<Text className="hidden md:block whitespace-nowrap font-sans font-bold text-xl text-secondary-black mb-4">
				{t('eligibleCandidates')}
			</Text>

			<div className="flex gap-4 justify-between mb-4">
				<div className="flex gap-4">
					<div className="sm:mt-0 md:mt-[16px] flex gap-4">
						{/* Back Button - Web */}
						<Button
							type="primary"
							className="hidden md:block font-bold text-sm"
							onClick={handleBackClick}
						>
							<span className="flex gap-2">
								<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
								{t('back')}
							</span>
						</Button>

						{/* Back Button - Mobile */}
						<div className="flex gap-3 sm:hidden">
							<a
								onClick={handleBackClick}
								className="relative top-[5px] md:top-[3px] mr-2 font-bold text-sm"
							>
								<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
							</a>
							<Text className="whitespace-nowrap font-sans font-bold text-xl text-secondary-black mb-4">
								{t('batch')}
							</Text>
						</div>
					</div>
					<div className="sm:mt-0 md:mt-[16px] flex gap-4 sm:justify-start md:justify-end">
						{/* Filter Button - Desktop */}
						<div
							className={`flex ${
								isLengthyLanguage ? 'flex-col-reverse gap-2' : 'flex-row'
							}`}
						>
							{isFilterApplied ? (
								<Button
									className="hidden sm:block flex-1 h-9 text-red-500 font-sans font-bold"
									type="link"
									onClick={handleResetFilters}
								>
									<span>
										{t('resetFilters')}
										<UndoOutlined className="ml-2" />
									</span>
								</Button>
							) : (
								''
							)}
							<Button
								className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid ${
									isFilterApplied ? 'border-primary-blue' : 'border-gray-200'
								}`}
								onClick={handleFilterPopup}
							>
								{isFilterApplied ? t('filterApplied') : t('filter')}
							</Button>
						</div>

						{/* Filter Icon - Mobile */}
						<Button
							className="block sm:hidden"
							variant="outlined"
							onClick={handleFilterPopup}
						>
							<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
						</Button>
					</div>
				</div>

				<div className="flex">
					<Select
						className="w-48"
						mode="multiple"
						options={batchOptions}
						value={selectedBatchId}
						onChange={handleBatchChange}
						size="small"
					/>
				</div>
			</div>

			<div className="flex flex-col gap-6">
				{isEmpty(applicationSummary) ? (
					<EmptyList message={t('noApplications')} />
				) : (
					<EligibleCandidatesTable
						applicationSummary={applicationSummary}
						pagination={pagination}
						paging={applicationPaging}
						onSubmitClick={onSubmitClick}
						setEligibleCandidatesIds={setEligibleCandidatesIds}
						onPageChange={onApplicationPageChange}
					/>
				)}
			</div>
			<FilterEligibleCandidates
				open={isFilterModalOpen}
				eligibleCandidateValue={eligibleCandidatesFilter}
				onClose={handleFilterModalClose}
				onEligibleCandidateFilterChange={onEligibleCandidatesChange}
				batchIds={selectedBatchId}
				resetTrigger={resetKey}
			/>
		</div>
	);
};
